import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Colors } from '@/constants/colors';

interface FormattedMessageProps {
  content: string;
  mentionedUsers?: Array<{ user_id: string; name: string; position: number }>;
  currentUserId?: string;
}

/**
 * FormattedMessage Component
 * Renders AI responses with smart markdown formatting
 * Lightweight custom parser for common markdown patterns
 * Also highlights @mentions
 */
export const FormattedMessage: React.FC<FormattedMessageProps> = ({
  content,
  mentionedUsers = [],
  currentUserId,
}) => {
  const renderContent = () => {
    const lines = content.split('\n');
    const elements: React.ReactElement[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Headings (## Heading)
      if (line.startsWith('### ')) {
        elements.push(
          <Text key={key++} style={styles.heading3}>
            {parseInlineFormatting(line.substring(4), mentionedUsers, currentUserId)}
          </Text>
        );
        continue;
      }
      if (line.startsWith('## ')) {
        elements.push(
          <Text key={key++} style={styles.heading2}>
            {parseInlineFormatting(line.substring(3), mentionedUsers, currentUserId)}
          </Text>
        );
        continue;
      }
      if (line.startsWith('# ')) {
        elements.push(
          <Text key={key++} style={styles.heading1}>
            {parseInlineFormatting(line.substring(2), mentionedUsers, currentUserId)}
          </Text>
        );
        continue;
      }

      // Bullet points (- item or * item)
      if (line.match(/^[\s]*[-*]\s+/)) {
        const bulletText = line.replace(/^[\s]*[-*]\s+/, '');
        elements.push(
          <View key={key++} style={styles.bulletItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>
              {parseInlineFormatting(bulletText, mentionedUsers, currentUserId)}
            </Text>
          </View>
        );
        continue;
      }

      // Numbered lists (1. item)
      if (line.match(/^[\s]*\d+\.\s+/)) {
        const match = line.match(/^[\s]*(\d+)\.\s+(.+)$/);
        if (match) {
          const [, number, text] = match;
          elements.push(
            <View key={key++} style={styles.bulletItem}>
              <Text style={styles.numberBullet}>{number}.</Text>
              <Text style={styles.bulletText}>
                {parseInlineFormatting(text, mentionedUsers, currentUserId)}
              </Text>
            </View>
          );
        }
        continue;
      }

      // Code blocks (```code```)
      if (line.trim().startsWith('```')) {
        const codeLines: string[] = [];
        i++; // Skip the opening ```
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        if (codeLines.length > 0) {
          elements.push(
            <View key={key++} style={styles.codeBlock}>
              <Text style={styles.codeText}>{codeLines.join('\n')}</Text>
            </View>
          );
        }
        continue;
      }

      // Empty lines (spacing)
      if (line.trim() === '') {
        elements.push(<View key={key++} style={styles.spacing} />);
        continue;
      }

      // Regular paragraph
      elements.push(
        <Text key={key++} style={styles.paragraph}>
          {parseInlineFormatting(line, mentionedUsers, currentUserId)}
        </Text>
      );
    }

    return elements;
  };

  return <View>{renderContent()}</View>;
};

/**
 * Parse inline markdown formatting (bold, italic, inline code, @mentions)
 * Returns array of Text components with proper styling
 */
function parseInlineFormatting(
  text: string,
  mentionedUsers: Array<{ user_id: string; name: string; position: number }> = [],
  currentUserId?: string
): React.ReactNode {
  // If no markdown patterns or mentions found, return plain text
  if (!text.includes('**') && !text.includes('*') && !text.includes('`') && !text.includes('@')) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyCounter = 0;

  while (remaining.length > 0) {
    // Try to find the next markdown pattern or mention
    let found = false;

    // Check for @mention (highest priority to avoid conflicts)
    const mentionMatch = remaining.match(/^(.*?)@(\w+)/);
    if (mentionMatch) {
      const [fullMatch, before, username] = mentionMatch;
      const mention = mentionedUsers.find(
        m => m.name.toLowerCase().replace(/\s+/g, '') === username.toLowerCase()
      );

      if (mention) {
        // This is a valid mention
        if (before) {
          parts.push(<Text key={`plain-${keyCounter++}`}>{before}</Text>);
        }

        const isMentioningMe = mention.user_id === currentUserId;
        parts.push(
          <Text
            key={`mention-${keyCounter++}`}
            style={isMentioningMe ? styles.mentionMe : styles.mention}
          >
            @{username}
          </Text>
        );
        remaining = remaining.substring(fullMatch.length);
        found = true;
        continue;
      }
    }

    // Check for **bold**
    const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*/);
    if (boldMatch) {
      const [fullMatch, before, boldText] = boldMatch;
      if (before) {
        parts.push(<Text key={`plain-${keyCounter++}`}>{before}</Text>);
      }
      parts.push(
        <Text key={`bold-${keyCounter++}`} style={styles.bold}>
          {boldText}
        </Text>
      );
      remaining = remaining.substring(fullMatch.length);
      found = true;
      continue;
    }

    // Check for *italic* (but not part of **)
    const italicMatch = remaining.match(/^(.*?)\*([^*]+)\*/);
    if (italicMatch && !remaining.startsWith('**')) {
      const [fullMatch, before, italicText] = italicMatch;
      if (before) {
        parts.push(<Text key={`plain-${keyCounter++}`}>{before}</Text>);
      }
      parts.push(
        <Text key={`italic-${keyCounter++}`} style={styles.italic}>
          {italicText}
        </Text>
      );
      remaining = remaining.substring(fullMatch.length);
      found = true;
      continue;
    }

    // Check for `code`
    const codeMatch = remaining.match(/^(.*?)`([^`]+)`/);
    if (codeMatch) {
      const [fullMatch, before, codeText] = codeMatch;
      if (before) {
        parts.push(<Text key={`plain-${keyCounter++}`}>{before}</Text>);
      }
      parts.push(
        <Text key={`code-${keyCounter++}`} style={styles.inlineCode}>
          {codeText}
        </Text>
      );
      remaining = remaining.substring(fullMatch.length);
      found = true;
      continue;
    }

    // No pattern found, add rest as plain text
    if (!found) {
      parts.push(<Text key={`plain-${keyCounter++}`}>{remaining}</Text>);
      break;
    }
  }

  return parts.length > 0 ? parts : text;
}

const styles = StyleSheet.create({
  // Headings
  heading1: {
    color: Colors.ai.primary,
    fontSize: 20,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 8,
    lineHeight: 26,
  },
  heading2: {
    color: Colors.ai.primary,
    fontSize: 18,
    fontWeight: '700',
    marginTop: 10,
    marginBottom: 6,
    lineHeight: 24,
  },
  heading3: {
    color: Colors.ai.primary,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 5,
    lineHeight: 22,
  },

  // Paragraphs
  paragraph: {
    color: Colors.text.primary,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
  },

  // Bullet lists
  bulletItem: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bullet: {
    color: Colors.ai.primary,
    fontSize: 16,
    marginRight: 8,
    marginTop: 2,
  },
  numberBullet: {
    color: Colors.ai.primary,
    fontSize: 15,
    fontWeight: '600',
    marginRight: 8,
    minWidth: 24,
  },
  bulletText: {
    flex: 1,
    color: Colors.text.primary,
    fontSize: 15,
    lineHeight: 22,
  },

  // Inline formatting
  bold: {
    fontWeight: '700',
    color: Colors.accent.primary,
  },
  italic: {
    fontStyle: 'italic',
    color: Colors.text.secondary,
  },
  inlineCode: {
    backgroundColor: Colors.background.tertiary,
    color: Colors.ai.primary,
    fontFamily: 'monospace',
    fontSize: 13,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 3,
  },

  // Code blocks
  codeBlock: {
    backgroundColor: Colors.background.tertiary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    borderRadius: 8,
    padding: 12,
    marginVertical: 8,
  },
  codeText: {
    color: Colors.text.primary,
    fontFamily: 'monospace',
    fontSize: 13,
    lineHeight: 18,
  },

  // Spacing
  spacing: {
    height: 8,
  },

  // Mentions
  mention: {
    backgroundColor: `${Colors.accent.primary}20`,
    color: Colors.accent.primary,
    fontWeight: '600',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  mentionMe: {
    backgroundColor: `${Colors.status.success}25`,
    color: Colors.status.success,
    fontWeight: '700',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
});
